import type { RequestContext } from "../context/request-context.js";
import type { IntegrityCatalogRepository } from "../../infrastructure/database/memory/in-memory-integrity-catalog-repository.js";
import {
  lookupAllowableStress,
  lookupCouponDensity,
  lookupQualityFactor,
  lookupWeldJointFactor,
  lookupYCoefficient,
  matchMaterialFromRegistry,
  type CatalogLookup,
  type CouponMaterial,
  type IntegrityCatalogData,
  type NormEdition,
  type PipingMaterial
} from "../../domain/engineering/integrity-catalog.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";

/** Parâmetros normativos do cálculo ASME B31.3, cada um com a sua origem. */
export interface AsmeNormativeParameters {
  material: PipingMaterial | null;
  editionId: string;
  allowableStress: CatalogLookup<number> | null;
  qualityFactor: CatalogLookup<number> | null;
  weldJointFactor: CatalogLookup<number> | null;
  yCoefficient: CatalogLookup<number> | null;
  /** Campos que a norma/catálogo não resolveu e que bloqueiam o cálculo automático. */
  unresolved: string[];
  /** Mensagem específica quando S não foi encontrado (regra "não inventar S"). */
  allowableStressMessage: string | null;
}

export const ALLOWABLE_STRESS_NOT_FOUND =
  "Tensão admissível não encontrada para o material/temperatura selecionados.";

/**
 * Porta de entrada única dos catálogos normativos. Toda consulta de S, E, W,
 * Y, densidade de cupom e edição de norma passa por aqui — nenhuma página lê
 * tabela diretamente nem carrega número fixo.
 */
export class IntegrityCatalogService {
  constructor(private readonly repo: IntegrityCatalogRepository) {}

  async getCatalog(context: RequestContext): Promise<IntegrityCatalogData> {
    this.requireRead(context);
    return this.repo.getCatalog();
  }

  async listMaterials(context: RequestContext): Promise<PipingMaterial[]> {
    this.requireRead(context);
    const catalog = await this.repo.getCatalog();
    return [...catalog.materials].sort((a, b) =>
      `${a.specification} ${a.grade}`.localeCompare(`${b.specification} ${b.grade}`, "pt-BR")
    );
  }

  async listCouponMaterials(context: RequestContext): Promise<CouponMaterial[]> {
    this.requireRead(context);
    const catalog = await this.repo.getCatalog();
    return [...catalog.couponMaterials].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }

  async listEditions(context: RequestContext, norm: string): Promise<NormEdition[]> {
    this.requireRead(context);
    const catalog = await this.repo.getCatalog();
    return catalog.editions.filter(e => e.norm === norm);
  }

  async defaultEdition(context: RequestContext, norm: string): Promise<NormEdition | null> {
    const editions = await this.listEditions(context, norm);
    return editions.find(e => e.isDefault) ?? editions[0] ?? null;
  }

  /** Material do catálogo correspondente ao que está escrito no cadastro da linha. */
  async matchMaterial(
    context: RequestContext,
    registryText: string | null,
    specification?: string | null,
    grade?: string | null
  ): Promise<PipingMaterial | null> {
    this.requireRead(context);
    const catalog = await this.repo.getCatalog();
    return matchMaterialFromRegistry(catalog.materials, registryText, specification, grade);
  }

  async resolveCouponDensity(context: RequestContext, couponMaterialId: string | null): Promise<CatalogLookup<number> | null> {
    this.requireRead(context);
    const catalog = await this.repo.getCatalog();
    return lookupCouponDensity(catalog.couponMaterials, couponMaterialId);
  }

  /**
   * S, E, W e Y do material na temperatura de projeto.
   *
   * Sem correspondência de material + temperatura + edição, S volta null e
   * `allowableStressMessage` traz a mensagem exata que a tela deve exibir —
   * nenhum valor é assumido.
   */
  async resolveAsmeParameters(
    context: RequestContext,
    input: { materialId: string | null; temperatureCelsius: number | null; editionId: string }
  ): Promise<AsmeNormativeParameters> {
    this.requireRead(context);
    const catalog = await this.repo.getCatalog();

    const material = input.materialId ? catalog.materials.find(m => m.id === input.materialId) ?? null : null;
    const unresolved: string[] = [];

    const allowableStress = material
      ? lookupAllowableStress(catalog.allowableStressTables, material.id, input.temperatureCelsius, input.editionId)
      : null;
    const qualityFactor = material ? lookupQualityFactor(catalog.qualityFactors, material.productForm) : null;
    const weldJointFactor = material
      ? lookupWeldJointFactor(catalog.weldJointFactors, material.materialClass, input.temperatureCelsius)
      : null;
    const yCoefficient = material
      ? lookupYCoefficient(catalog.yCoefficients, material.materialClass, input.temperatureCelsius)
      : null;

    if (!material) unresolved.push("Material");
    if (!allowableStress) unresolved.push("Tensão admissível (S)");
    if (!qualityFactor) unresolved.push("Fator de qualidade (E)");
    if (!weldJointFactor) unresolved.push("Fator da junta soldada (W)");
    if (!yCoefficient) unresolved.push("Coeficiente Y");

    return {
      material,
      editionId: input.editionId,
      allowableStress,
      qualityFactor,
      weldJointFactor,
      yCoefficient,
      unresolved,
      allowableStressMessage: material && !allowableStress ? ALLOWABLE_STRESS_NOT_FOUND : null
    };
  }

  /** Cadastro/edição das tabelas pela engenharia — é o que evita "consertar" valores dentro da página. */
  async saveCatalog(context: RequestContext, data: IntegrityCatalogData): Promise<void> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.CatalogUpdate);
    await this.repo.saveCatalog(data);
  }

  private requireRead(context: RequestContext): void {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.CalculationAccess);
  }
}
